import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ScannerProvider } from "./context/ScannerContext";
import ScanPhonePage from "./pages/ScanPhonePage";
import { useEffect, useState } from "react";
import { useTelephony } from "./hooks/useTelephony";
import IncomingCallPopup from "./components/Telephony/IncomingCallPopup";
import ClientNameModal from "./components/Telephony/ClientNameModal";
import StockShortageModal, { Shortage } from "./components/deal/StockShortageModal";
import WebRTCPhone from "./components/Telephony/WebRTCPhone";
import { WebRTCProvider, useWebRTCContext } from "./context/WebRTCContext";
import "./styles/mobile.css";
import "./styles/mobile-polish.css";

import SidebarMenu from "./components/layout/SidebarMenu";
import TopBar from "./components/layout/TopBar";
import LoginForm from "./components/auth/LoginForm";

import ClientsPage from "./pages/ClientsPage";
import WhatsappPage from "./pages/WhatsappPage";
import ClientPage from "./pages/ClientPage";
import ProductPage from "./pages/ProductPage";
import PrintPage from "./pages/PrintPage";
import KanbanPage from "./components/kanban/Kanban";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/profile/ProfilePage";
import WarehousePage from "./pages/WarehousePage";
import { CompaniesTab } from "./components/Companies/CompaniesTab";

import FinishedDeals from "./components/deal/FinishedDeals";
import IntegrationsPage from "./pages/IntegrationsPage"
import CreditPage from "./pages/CreditPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import ReportsPage from "./pages/ReportsPage"
import AlStyleCatalogPage from "./pages/AlStyleCatalogPage"
import DashboardPage from "./pages/DashboardPage"
import WorkOrdersPage from "./pages/WorkOrdersPage"
import CashDebtsPage from "./pages/CashDebtsPage"
import PosPage, { CashierShell } from "./pages/PosPage"
import PosReportPage from "./pages/PosReportPage"
import PromotionsPage from "./pages/PromotionsPage"
import ApprovalsPage from "./pages/ApprovalsPage"
import KeysPage from "./pages/KeysPage"
import BottomNav from "./components/layout/BottomNav"
import { getRole } from "./hooks/usePermissions"

import DesktopStatus from "./desktop/DesktopStatus";

import "./styles/index.css";

async function getUserByToken(token: string) {
  try {
    const res = await fetch("/api/auth/me", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    const skip = ['/login', '/reset-password'];
    if (!skip.some(p => location.pathname.startsWith(p))) {
      localStorage.setItem('lastPage', location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function AppInner() {
  const { answer, callState, error } = useWebRTCContext();
  return <AppContent answer={answer} callState={callState} webrtcError={error} />;
}

export default function App() {
  const loc = useLocation();
  // страница сканера на телефоне открывается по QR без входа в CRM и без телефонии
  if (loc.pathname.startsWith("/scan/")) return <Routes><Route path="/scan/:code" element={<ScanPhonePage />} /></Routes>;
  return <WebRTCProvider><AppInner /></WebRTCProvider>;
}

function AppContent({ answer, callState, webrtcError }: { answer: () => void; callState: string; webrtcError?: string }) {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [pendingCall, setPendingCall] = useState<string | undefined>();
  const [shortages, setShortages] = useState<Shortage[] | null>(null);

  // Окно «не хватает на складе» при попытке завершить сделку (событие шлёт api/axios.ts)
  useEffect(() => {
    const h = (e: Event) => setShortages((e as CustomEvent<Shortage[]>).detail);
    window.addEventListener("stock-shortage", h);
    return () => window.removeEventListener("stock-shortage", h);
  }, []);
  const { incomingCall, dismissCall, answerCall, namePrompt, closeNamePrompt } = useTelephony(getRole() !== "CASHIER");

  // Listen for click-to-call events dispatched by ClickToCall component
  useEffect(() => {
    const handler = (e: CustomEvent<{ phone: string }>) => {
      setPendingCall(e.detail.phone);
      setShowPhone(true);
    };
    window.addEventListener('crm:call', handler as EventListener);
    return () => window.removeEventListener('crm:call', handler as EventListener);
  }, []);

  const theme = "dark";
  const setTheme = (_t: "light" | "dark") => {};

  useEffect(() => {
    document.body.classList.remove("theme-light", "theme-dark");
    document.body.classList.add("theme-dark");
  }, []);

  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  );
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      const u = await getUserByToken(token);
      if (!u) {
        localStorage.removeItem("token");
        setToken(null);
        setLoading(false);
        return;
      }

      setUser(u);
      setLoading(false);
    };

    check();
  }, [token]);

  const handleLogin = (newToken: string) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    const lastPage = localStorage.getItem("lastPage") || "/deals";
    navigate(lastPage);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    navigate("/login");
  };

  if (loading) return null;

  return (
    <>
      <DesktopStatus />
      {!token || !user ? (
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="*"
            element={
              <LoginForm onLogin={handleLogin} />
            }
          />
        </Routes>
      ) : user.role === "CASHIER" ? (
        <ScannerProvider><CashierShell user={user} onLogout={handleLogout} /></ScannerProvider>
      ) : (
        <ScannerProvider>
        <div className="layout">
          <PageTracker />
          {incomingCall && (
            <IncomingCallPopup
              call={incomingCall}
              onDismiss={dismissCall}
              onAnswer={callState === 'ringing' ? answer : undefined}
              onAnswerAMI={(ext) => answerCall(incomingCall.callId, ext)}
              webrtcError={webrtcError}
            />
          )}
          {shortages && (
            <StockShortageModal
              shortages={shortages}
              onClose={() => setShortages(null)}
              onOpenWarehouse={() => { setShortages(null); navigate("/warehouse"); }}
            />
          )}
          {namePrompt && (
            <ClientNameModal
              clientId={namePrompt.clientId}
              dealId={namePrompt.dealId}
              phone={namePrompt.phone}
              onClose={closeNamePrompt}
            />
          )}
          {showPhone && (
            <WebRTCPhone
              onClose={() => setShowPhone(false)}
              externalCall={pendingCall}
              onExternalCallHandled={() => setPendingCall(undefined)}
            />
          )}
          {/* Мобильный оверлей */}
          {sidebarOpen && (
            <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
          )}

          <SidebarMenu
            theme={theme}
            setTheme={setTheme}
            user={user}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <div className="content-area">
            <TopBar
              theme={theme}
              setTheme={setTheme}
              onLogout={handleLogout}
              user={user}
              onBurgerClick={() => setSidebarOpen(o => !o)}
              onPhoneClick={() => setShowPhone(p => !p)}
            />

            <Routes>
              <Route path="/dashboard" element={user.role === "ADMIN" ? <DashboardPage /> : <Navigate to="/deals" />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/pos" element={["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(user.role) ? <PosPage /> : <Navigate to="/deals" />} />
              <Route path="/pos-report" element={["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(user.role) ? <PosReportPage /> : <Navigate to="/deals" />} />
              <Route path="/promotions" element={["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(user.role) ? <PromotionsPage /> : <Navigate to="/deals" />} />
              <Route path="/approvals" element={user.role === "ADMIN" ? <ApprovalsPage /> : <Navigate to="/deals" />} />
              <Route path="/keys" element={["ADMIN", "OFFICE_LEAD", "SENIOR_OFFICE"].includes(user.role) ? <KeysPage /> : <Navigate to="/deals" />} />
              <Route path="/whatsapp" element={<WhatsappPage />} />
              <Route path="/clients/:id" element={<ClientPage />} />
              <Route path="/products" element={<ProductPage />} />
              <Route path="/print" element={<PrintPage />} />
              <Route path="/deals" element={<KanbanPage />} />

              {/* 🔥 Только для ADMIN */}
              <Route
                path="/deals/finished"
                element={
                  user.role === "ADMIN" ? (
                    <FinishedDeals />
                  ) : (
                    <Navigate to="/deals" />
                  )
                }
              />
              {/* 🔥 Только для ADMIN */}
              <Route
                path="/integrations"
                element={
                  user.role === "ADMIN" ? (
                    <IntegrationsPage />
                  ) : (
                    <Navigate to="/deals" />
                  )
                }
              />

              <Route
                path="/al-catalog"
                element={user.role === "ADMIN" ? <AlStyleCatalogPage /> : <Navigate to="/deals" />}
              />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/warehouse" element={<WarehousePage />} />
              <Route path="/work-orders" element={<WorkOrdersPage user={user} />} />
              <Route path="/cash-debts" element={<CashDebtsPage user={user} />} />
              <Route path="/companies" element={<CompaniesTab />} />
              <Route path="/site-customers" element={<Navigate to="/clients?filter=orders" replace />} />

              <Route
                path="/credit"
                element={user.role === "ADMIN" ? <CreditPage /> : <Navigate to="/deals" />}
              />

              <Route path="/reports" element={<ReportsPage />} />
              <Route path="*" element={<Navigate to="/deals" />} />
            </Routes>
          </div>

          <BottomNav user={user} onLogout={handleLogout} />
        </div>
        </ScannerProvider>
      )}
    </>
  );
}
